import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  GuestsUsedCell,
  RateBadge,
  RevenueCell,
  ServiceNameCellContent,
} from "./topServicesTableCells";
import type { ServiceCategoryFilter, TopServiceRow } from "./mockData";
import { resolveTopServicesAmountLabel } from "./servicesTabMappers";

interface ServicesAllListTableProps {
  rows: TopServiceRow[];
  /** Segment of the rows: it names the amount column (Earned or Revenue). */
  segment: ServiceCategoryFilter;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (row: TopServiceRow) => void;
}

export const ServicesAllListTable: React.FC<ServicesAllListTableProps> = ({
  rows,
  segment,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const amountLabel = resolveTopServicesAmountLabel(segment);

  const columns: Column<TopServiceRow>[] = useMemo(
    () => [
      {
        key: "rate",
        header: t("analytics.common.rate", "Rate"),
        className: "w-[72px] text-center",
        render: (_, row) => (
          <div className="flex justify-center">
            <RateBadge rate={row.rate} />
          </div>
        ),
      },
      {
        key: "name",
        header: t("analytics.services.table.service", "Service"),
        className: "text-center min-w-[140px]",
        render: (_, row) => <ServiceNameCellContent row={row} />,
      },
      {
        key: "bookings",
        header: t("analytics.services.table.bookings", "Bookings"),
        className: "text-center text-[#171717]",
        render: (value) => <span>{value}</span>,
      },
      {
        key: "guestsUsedPercent",
        header: t(
          "analytics.services.table.guestsUsedPercent",
          "% Guests Used",
        ),
        className: "text-center min-w-[180px]",
        render: (_, row) => <GuestsUsedCell percent={row.guestsUsedPercent} />,
      },
      {
        key: "revenue",
        header: t(amountLabel.key, amountLabel.fallback),
        className: "text-center",
        render: (_, row) => <RevenueCell revenue={row.revenue} />,
      },
    ],
    [t, amountLabel],
  );

  return (
    <div className="overflow-x-auto bg-white">
      <DataTable
        data={rows}
        columns={columns}
        getRowKey={(row, index) => row.id ?? `out-of-catalog-${index}`}
        onRowClick={
          onRowClick
            ? (row) => {
                // A Guesteria Partners booking made outside the catalog has
                // no service to navigate to.
                if (row.id != null) {
                  onRowClick(row);
                }
              }
            : undefined
        }
        className={onRowClick ? "[&_tbody_tr]:cursor-pointer" : undefined}
        columnHeaderClassName="text-[10px] font-medium text-[#737373]"
        emptyMessage={t(
          "analytics.services.table.empty",
          "No services found matching your criteria.",
        )}
        withSorting={false}
        withPagination={totalPages > 1}
        pagination={
          totalPages > 1
            ? {
                currentPage,
                totalPages,
                onPageChange,
              }
            : undefined
        }
      />
    </div>
  );
};
